import React from 'react';
import {Button, EMPHASIS, Tooltip} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import {useReportBuilder} from '../ReportBuilderContext';
import {useBuilderShareDialog} from './useBuilderShareDialog';

/** The Share action, shared by both top bars so the two cannot drift apart.
 *
 * Renders nothing for a reader: sharing is an owner/editor action, and a reader's bar carries only
 * Download. On a report that has never been saved the button stays disabled behind a tooltip that
 * says why — there is no Reports-list entry to share yet, so the dialog would have nothing to open
 * against. The disabled button is wrapped in a span because a disabled button fires no pointer
 * events, and without a wrapper the tooltip would never appear on the one control that needs to
 * explain itself. */
export function ReportShareButton(): JSX.Element | null {
	const {formatMessage} = useIntl();
	const {role, savedReportId, updateSavedReportPrivateAccess} = useReportBuilder();
	const {open: openShare, loading: shareLoading} = useBuilderShareDialog(updateSavedReportPrivateAccess);

	if (role !== 'editor') {
		return null;
	}

	const label = formatMessage({id: 'ai_report_builder.share'});

	if (savedReportId === null) {
		return (
			<Tooltip content={formatMessage({id: 'ai_report_builder.share_requires_save'})}>
				<span>
					<Button
						emphasis={EMPHASIS.MONO}
						icon={'share'}
						disabled
						data-cy={'ai-report-share-button'}
						aria-label={label}
					>
						{label}
					</Button>
				</span>
			</Tooltip>
		);
	}

	return (
		<Button
			emphasis={EMPHASIS.MONO}
			icon={'share'}
			disabled={shareLoading}
			onPress={() => openShare(savedReportId)}
			data-cy={'ai-report-share-button'}
			aria-label={label}
		>
			{label}
		</Button>
	);
}
